import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  NotificationBatch,
  NotificationItem,
  NotificationsState,
  NotifListByBatchIdParams,
  NotifListParams,
} from "../../interfaces/notifications-interface";
import {
  getBatchNotif,
  getBatchNotifList,
  getNotifListByBatch,
  triggerBatchUpload,
} from "../../services/NotificationService";

export const listBatchNotifications = createAsyncThunk(
  "notifications/listBatchNotifications",
  async (params: NotifListParams, thunkAPI) => {
    const token = localStorage.getItem("notifIdToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const batchNotifList = await getBatchNotifList(params);
      return batchNotifList;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const viewBatchNotification = createAsyncThunk(
  "notifications/viewBatchNotifications",
  async (batchId: string, thunkAPI) => {
    const token = localStorage.getItem("notifIdToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const batchNotif = await getBatchNotif(batchId);
      return batchNotif;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const listBatchNotifItems = createAsyncThunk(
  "notifications/listBatchNotifItems",
  async (params: NotifListByBatchIdParams, thunkAPI) => {
    const token = localStorage.getItem("notifIdToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const batchNotifListItems = await getNotifListByBatch(params);
      return batchNotifListItems;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const triggerBatchNotifSending = createAsyncThunk(
  "notifications/triggerBatchNotifSending",
  async (batchId: string, thunkAPI) => {
    const token = localStorage.getItem("notifIdToken");

    if (!token) {
      return thunkAPI.rejectWithValue("No authentication token found.");
    }

    try {
      const uploadRes = await triggerBatchUpload(batchId);
      return uploadRes;
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const notificationsSlice = createSlice({
  name: "notifications",
  initialState: {
    batchNotifications: [] as NotificationBatch[],
    hasMoreBatches: false,
    selectedBatchNotif: null as NotificationBatch | null,
    notifItemForSelectedBatch: [] as NotificationItem[],
    totalNotifItem: 0,
    totalNotifItemPages: 0,
    status: "idle",
    error: null,
  } as NotificationsState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(listBatchNotifications.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listBatchNotifications.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.batchNotifications = action.payload.batches;
        state.hasMoreBatches = action.payload.hasMore;
        state.error = null;
      })
      .addCase(listBatchNotifications.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Failed to list batch notifications";
      })
      .addCase(viewBatchNotification.pending, (state) => {
        state.status = "loading";
      })
      .addCase(viewBatchNotification.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedBatchNotif = action.payload;
        state.error = null;
      })
      .addCase(viewBatchNotification.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload ?? "Failed to get batch notification";
      })
      .addCase(listBatchNotifItems.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listBatchNotifItems.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.notifItemForSelectedBatch = action.payload.notifications;
        state.totalNotifItem = action.payload.total;
        state.totalNotifItemPages = action.payload.totalPages;
        state.error = null;
      })
      .addCase(listBatchNotifItems.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ?? "Failed to list batch notification items";
      })
      .addCase(triggerBatchNotifSending.pending, (state) => {
        state.status = "loading";
      })
      .addCase(triggerBatchNotifSending.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selectedBatchNotif = state.selectedBatchNotif && {
          ...state.selectedBatchNotif,
          id: action.payload.response.batchId,
          status: "processing",
        };
        state.error = null;
      })
      .addCase(triggerBatchNotifSending.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ?? "Failed to trigger batch notification sending";
      });
  },
});

export default notificationsSlice.reducer;
